import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Alert, Layout, Form, Card, Row, Col, Input, Radio, Button, 
    InputNumber, Affix, Space, message, Popover, Select, DatePicker } from 'antd';
import { useCreateLearningObjectMutation, 
    useLocalesQuery, 
    usePatchLearningObjectMutation } from '../../gql/hooks';
import { PageHeader } from '@ant-design/pro-layout';
import { useDeleteLearningObjectMutation } from '../../gql/hooks/mutations';
import { LEARNING_OBJECT_QUERY, LXP_FILE_QUERY, COMPANY_INTEGRATIONS_QUERY, GET_PROXY_URL_QUERY, SCORM_COURSE_QUERY } from '../../gql/queries';
import { useParam, useCreateEditLocales, useUploadLXPFile, useCreateURLAsset, 
    useCreateProxyAsset, useCreateProxyObject, usePatchProxyObject } from '../../hooks';
import { LearningObjectUtil } from '../../gql/utils';
import { buildPageHeaderBreadcrumb } from '../../updated/utils'; 
import { v4 as uuidv4 } from 'uuid';
import { Routes } from '../../routes';
import { DEFAULT_LOCALE_CODE } from '../../const';
import { Section } from '../../components/section/Section';
import RichTextEditor from '../../components/controls/RichTextEditor/RichTextEditor';
import ApolloError from '../../components/ApolloError'; 
import DeleteButton from '../../components/DeleteButton';
import AssetUpload from '../../components/formItems/assetUpload/AssetUpload';
import FileUpload from '../../components/formItems/FileUpload/FileUpload';
import { LanguageSelector } from '../../components/formItems';
import { useAuthState } from '../../auth/useAuthState';
import { Loadable } from '../../components/loadable/Loadable';
import { ErrorPopup } from '../../components/errorPopup/ErrorPopup';
import { flags } from '../../featureFlags/flags';
import { useFeatureFlag } from '../../featureFlags';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import TinyMCE from '../../components/TinyMCE/TinyMCE';    
import timezone from 'dayjs/plugin/timezone';
import './styles.less';

dayjs.extend( isSameOrBefore );
dayjs.extend( timezone ); 

const CreateEditObjectView = ( props ) => {
    const TinyMCERef = useRef( null );
    const showTinyMCE = useFeatureFlag( { name: flags.USE_TINY_MCE } );
    const { hasPermission } = useAuthState();
    const canDelete = hasPermission( 'delete-learning-object' );
    const canEdit = hasPermission( 'edit-learning-object' );
    const [ courseCount, setCourseCount ] = useState( 0 );
    const [ fileType, setFileType ] = useState( 'scorm' );
    const [ selectedFile, setSelectedFile ] = useState( );
    const [ uploadingFile, setUploadingFile ] = useState( false );
    const { uploadFile } = useUploadLXPFile();
    const showProxy = useFeatureFlag( { name: flags.SHOWPROXY } );
    const showSCORMandFILE = useFeatureFlag( { name: flags.SCORMANDFILE } );
    const [ hasFileError, setHasFileError ] = useState( false );
    const [ originalURL, setOriginalURL ] = useState();
    const { createURLAsset } = useCreateURLAsset();
    const { createProxyAsset } = useCreateProxyAsset();
    const { createProxyObject } = useCreateProxyObject();
    const { patchProxyObject } = usePatchProxyObject();
    const [ selectedIntegration, setSelectedIntegration ] = useState( );
    const [ originalProxyURLId, setOriginalProxyURLId ] = useState();
    const [ originalProxyAssetId, setOriginalProxyAssetId ] = useState();
    const [ proxySaveError, setProxySaveError ] = useState( false );
    const [ scormAssets, setScormAssets ] = useState();
    const [ hideDownloadButton, setHideDownloadButton ] = useState( false );
    const [ messageApi, contextHolder ] = message.useMessage();
  
    const initialFormValues = {
        name: '',
        description: '',
        format: 'scorm',
        duration: 10,
        expireAt: null,
        scormAssets: [],
        fileAssets: [],
        urlAssets: [],
        locale: DEFAULT_LOCALE_CODE
    };
    
    const formatTypes = useMemo( () => {
        const result = [ 'scorm' ];

        if ( showSCORMandFILE ) {
            result.push( 'file' );
            result.push( 'url' );
        }

        if ( showProxy ) {
            result.push( 'proxy' );
        }

        return result;
    } );
    
    // formController stores form values, validates, enforces rules etc.
    const [ formController ] = Form.useForm(); 

    const { data: allLocales, loading: localesLoading, error: localesError } = useLocalesQuery( { fetchPolicy: 'network-only' } );

    // Locales handler hook
    const [ currentLocaleCode, setLocales, descriptionRawHTML, handleChangeLocale, validateEnglishExists ] = useCreateEditLocales( formController, true );

    // Editing (true) or creating (false) an object 
    const [ isEditing, setIsEditing ] = useState( false );

    const [ originalName, setOriginalName ] = useState( '' );
    
    /**
     * Object ID handling 
     */ 
    
    // Check for the objectId in the route
    const { value: paramObjectId, checked: checkedRouteForId } = useParam( 'objectId' );
  
    // Set the objectId, either from the route or if that doesn't exist generate a new one  
    const objectId = useMemo( () => { 
        if ( checkedRouteForId ) {
            if ( paramObjectId ) { 
                setIsEditing( true );
                formController.setFields( [ { name: 'id', value: paramObjectId } ] ); 
                return paramObjectId;
            } else {
                setIsEditing( false );
                const uuid = uuidv4();
                formController.setFields( [ { name: 'id', value: uuid } ] ); 
                return uuid;
            }
        } else {
            return undefined;
        }
    }, [ paramObjectId, checkedRouteForId, formController ] );

    /**
     * Proxy handling
     */
    
    const [ fetchCompanyIntegrations, { 
        data: companyIntegrationsData, loading: loadingCompanyIntegrations, 
        error: errorCompanyIntegrations, called: calledCompanyIntegrations } ] = 
        useLazyQuery( COMPANY_INTEGRATIONS_QUERY, { fetchPolicy: 'network-only' } );

    // Fetch company integrations
    useEffect( () => {
        if ( showProxy ) {
            fetchCompanyIntegrations();
        }
    }, [ showProxy ] );

    const proxyCompanyIntegrations = useMemo( () => {
        if ( companyIntegrationsData && calledCompanyIntegrations && 
            !loadingCompanyIntegrations && !errorCompanyIntegrations ) {
             
            return companyIntegrationsData.companyIntegrations.edges.map( ( i => {
                return {  
                    ...i.node.integrationType,
                    id: i.node.id,
                    integrationTypeId: i.node.integrationTypeId,
                };
            } ) );
        } else {
            return [];
        }
    }, [ companyIntegrationsData, calledCompanyIntegrations, 
        errorCompanyIntegrations, loadingCompanyIntegrations ] );

    const [ fetchProxyObject, { data: proxyObjectData } ] = useLazyQuery( GET_PROXY_URL_QUERY, 
        { variables: { proxyObjectID: originalProxyURLId } }, { fetchPolicy: 'network-only' } );
    
    const originalProxyObject = useMemo( ()=> {
        return proxyObjectData?.proxyObject || undefined;
    }, [ proxyObjectData ] );
    
    useEffect( () => {
        if ( originalProxyObject && proxyCompanyIntegrations.length > 0 ) {
            const integration = proxyCompanyIntegrations.find( ( i, index ) => {
                if ( i.id === originalProxyObject.companyIntegration.id ) {
                    setSelectedIntegration( index );
                    return true;
                } else {
                    return false;
                }
            } );
            
            if ( integration ) {
                formController.setFields(
                    Object.keys( originalProxyObject.fieldData ).map( key => {
                        return { name: key, value: originalProxyObject.fieldData[ key ] }; 
                    } ) ); 
            }
        }
    }, [ originalProxyObject, proxyCompanyIntegrations ] );

    const getProxyItemRules = ( item ) => {
        const result = [];

        if ( item.required === true ) {
            result.push( {
                required: true,
                message: item?.validation?.errorMessage || 'You must fill in this field.',
            } );
        }

        if ( item.inputType === 'url' ) {
            result.push( {
                message: item?.validation?.errorMessage || 'You need to add a valid URL starting with http:// or https://.',
                validateTrigger: [],
                validator: ( _, value ) => {
                    if ( value === '' ) {
                        return Promise.resolve();
                    } 

                    try {
                        new URL( value );
                        return Promise.resolve();
                    } catch {
                        return Promise.reject();
                    }
                }
            } );
        }

        return result;
    };

    const handleIntegrationChange = ( e ) => {
        setSelectedIntegration( e );
    };

    /**
     * File handling
     */

    const handleTypeChange = ( type ) => {
        setFileType( type );
        if ( type === 'proxy' && !selectedIntegration ) {
            setSelectedIntegration( 0 );
        }
    };

    const handleSelectFile = ( file ) => {
        if ( !file ) {
            formController.setFields( [ { name: 'fileAssets', value: [] } ] );  
        }
        setHideDownloadButton( true );
        setHasFileError( false );
        setSelectedFile( file );
    };

    /**
     * Delete Learning Object handling
     */

    const [ deleteItem ] = useDeleteLearningObjectMutation();

    const handleDelete = useCallback( async () => {
        await deleteItem( { variables: { objectID: objectId } } );
        window.history.back();
    }, [ objectId, deleteItem ] );

    /**
     * Fetch Object handling 
     */ 
    
    const [ fetchLearningObject, { data: fetchLearningObjectData, loading: fetchLearningObjectLoading, 
        error: fetchLearningObjectError, called: fetchLearningObjectCalled  } ] = useLazyQuery( LEARNING_OBJECT_QUERY, { fetchPolicy: 'network-only' } );
    
    const [ createLearningObject, { loading: createLoading, called: createCalled, 
        error: createError } ] = useCreateLearningObjectMutation();

    // Fetches object if isEditing
    useEffect( () => {
        if ( isEditing && objectId && !createCalled ) {
            fetchLearningObject( { variables: { objectID: objectId } } );
        } 
    }, [ fetchLearningObject, createCalled, isEditing, objectId ] );

    // Use callback so useEffect can call async method
    const setLocaleData = useCallback( async ( objectData ) => {
        setLocales( await LearningObjectUtil.getLocales( objectData ) );
    }, [ setLocales ] );

    // After fetch
    useEffect( () => {
        const data = fetchLearningObjectData?.object;
        if ( !data ) { return; }
        
        let url;
        let urls = LearningObjectUtil.getURLAssets( data );
        if ( urls.length > 0 ) {
            url = urls[ 0 ].url;
            setOriginalURL( urls[ 0 ] );
        }
                  
        // Fetch proxy Object
        let proxyUrls = LearningObjectUtil.getProxyURLAssets( data );
        if ( proxyUrls.length > 0 ) {
            setOriginalProxyURLId( proxyUrls[ 0 ].name );
            setOriginalProxyAssetId( proxyUrls[ 0 ].id );
            fetchProxyObject();
        }

        formController.setFields( [
            { name: 'format', value: LearningObjectUtil.getFormat( data ) },
            { name: 'duration', value: LearningObjectUtil.getDuration( data ) },
            { name: 'expireAt', value: LearningObjectUtil.getExpireAt( data ) },
            { name: 'scormAssets', value: LearningObjectUtil.getScormAssets( data ) },
            { name: 'fileAssets', value: LearningObjectUtil.getFileAssets( data ) },
            { name: 'urlAssets', value: urls },
            { name: 'url', value: url } ]
        );   
        
        setScormAssets(  LearningObjectUtil.getScormAssets( data ) );
        setFileType( LearningObjectUtil.getFormat( data ) );
        setCourseCount( LearningObjectUtil.getCourseCount( data ) ) ;
        setOriginalName( LearningObjectUtil.getName( data ) );
        setLocaleData( data );
    }, [ fetchLearningObjectData, formController, setLocaleData ] );

    /******************************
     * Fetch lxpFileId URL
     ******************************/
       
    const lxpFileId = useMemo( () => {
        const data = fetchLearningObjectData?.object;
        if ( !data ) { return undefined; }
       
        const fileAssets = LearningObjectUtil.getFileAssets( data );

        if ( fileAssets && fileAssets.length > 0 ) {
            return fileAssets[ 0 ].lxpFileId;
        } else {
            return undefined;
        }
    }, [ fetchLearningObjectData ] );

    const [ fetchLxpFile, { data: lxpFileData } ] = 
        useLazyQuery( LXP_FILE_QUERY,  { variables: { lxpFileID: lxpFileId } }, { fetchPolicy: 'network-only' } );

    useEffect( () => {
        if ( lxpFileId ) {
            fetchLxpFile();
        }
    }, [ lxpFileId ] );

    const lxpFileURL = useMemo( () => {
        if ( lxpFileData && lxpFileData?.lxpFile?.node?.signedUrl ) {
            return lxpFileData?.lxpFile?.node?.signedUrl;
        } else {
            return undefined;
        }
    }, [ lxpFileData ] );

    /**********************************
     * Form Submission, patch & create 
     **********************************/
   
    const [ patchLearningObject, { loading: patchLoading, called: patchCalled,  
        error: patchError } ] = usePatchLearningObjectMutation();
   
    const handleFormSubmission =  async values  => {
        setUploadingFile( true );
 
        if ( fileType === 'file' ) {
            if ( !selectedFile &&  ( !values[ 'fileAssets' ] || values[ 'fileAssets' ].length === 0 ) ) {
                setHasFileError( true );
                setUploadingFile( false );
                messageApi.error( 'You must add a file to this learning object before saving it.' );
                return;
            }
        }

        if ( fileType === 'scorm' ) {
            if ( !values[ 'scormAssets' ] || values[ 'scormAssets' ].length === 0 ) {
                setHasFileError( true );
                messageApi.error( 'You must add a file to this learning object before saving it.' );
                setUploadingFile( false );
                return;
            }
        }

        const [ cmxLocales, nonCmxLocales ] = [
            ( allLocales?.locales?.edges ?? [] ).filter( ( { node } ) => node.cmxSupported ).map( ( { node } ) => node ), 
            ( allLocales?.locales?.edges ?? [] ).filter( ( { node } ) => !node.cmxSupported ).map( ( { node } ) => node ) ];
                
        // This saves the current name and description fields to the locales array
        let formLocales = handleChangeLocale( currentLocaleCode );

        // Format the locale descriptions to remove any line breaks. New text blocks and spacing will
        // be created through the HTML embedded in the strings, so we don't need the line breaks. Also,
        // we want to get rid of spaces that may exist between the HTML tags.
        formLocales = formLocales
            .filter( loc => loc.name && loc.description )
            .map( ( localeData ) => ( {
                ...localeData,
                description: localeData.description.replace( /[\n\r]/g, '' ).replace( />\s+</g, '><' ),
            } ) );

        const previousData = fetchLearningObjectData?.object;
        const d = values[ 'duration' ];

        let fileAssetId;
        
        if ( selectedFile && fileType === 'file' ) {
            setUploadingFile( true );

            // Upload file 
            const response = await uploadFile( { file: selectedFile, createFileAsset: true } );
            const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
            
            if ( success ) {
                fileAssetId = response;
                setUploadingFile( false );
            } else {
                messageApi.success( 'An error occurred uploading your file. Please reload this page and try again.' );
                setUploadingFile( false );
                return;
            } 
        }
        
        if ( fileType === 'url' ) {
            let u = values[ 'url' ];
             
            if ( originalURL && originalURL.url === u ) {
                fileAssetId = originalURL.id;
            } else {
                setUploadingFile( true );
    
                // Upload file 
                const response = await createURLAsset( { name: u, url: u } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
                
                if ( success ) {
                    fileAssetId = response;
                    setUploadingFile( false );
                } else {
                    messageApi.success( 'An error occurred saving your URL. Please reload this page and try again.' );
                    setUploadingFile( false );
                    return;
                }
            }
        }
        
        let proxyObjectId;

        if ( fileType === 'proxy' ) {

            // Create new proxy object if none exists
            if ( !originalProxyObject ) {
                const integrationId = proxyCompanyIntegrations[ selectedIntegration ].id;
                
                const fieldsData = Object.fromEntries( proxyCompanyIntegrations[ selectedIntegration ].fieldSchema.map( i => {
                    return [ i.key, values[ i.key ] ];
                } ) );

                // SAVE FOR TESTING
                //const fieldsData =  
                //{ 'componentTypeId': 'WBT', 'revisionDate': '1607032500000', 'componentId': 'COMPREHENSIVE_VEEVA_V2' };
                //{ "componentTypeId": "WBT", "revisionDate": "1612134000000", "componentId": "BAY_3280270" }
                
                setUploadingFile( true );
        
                const response = await createProxyObject( { companyIntegrationId: integrationId, fieldData: fieldsData } );
                const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
                
                if ( success ) { 
                    proxyObjectId = response; 
                    setProxySaveError( false );
                } else {
                    setProxySaveError( true );
                    document.getElementById( 'proxy-card' ).scrollIntoView();
                    setUploadingFile( false );
                    return;
                } 
                
                // Save asset 
                const responseAsset = await createProxyAsset( { proxyObjectId } );
                const successAsset = !( response?.error || response?.errors && response?.errors?.length > 0 );
                    
                if ( successAsset ) {
                    fileAssetId = responseAsset;
                     
                } else {
                    messageApi.success( 'An error occurred saving your asset. Please reload this page and try again.' );
                    setUploadingFile( false );
                    return;
                }
            } else 
            {
                const fieldsData = Object.fromEntries( proxyCompanyIntegrations[ selectedIntegration ].fieldSchema.map( i => {
                    return [ i.key, values[ i.key ] ];
                } ) );
                
                // check to see if same
                if ( fieldsData.componentId !== originalProxyObject.fieldData.componentId ||
                    fieldsData.revisionDate !== originalProxyObject.fieldData.revisionDate ||
                    fieldsData.componentTypeId !== originalProxyObject.fieldData.componentTypeId ||
                    originalProxyObject.companyIntegration.id !== proxyCompanyIntegrations[ selectedIntegration ].id
                ) {
                    fileAssetId = originalProxyAssetId; 
                    const integrationId = proxyCompanyIntegrations[ selectedIntegration ].id;
                
                    const fieldsData = Object.fromEntries( proxyCompanyIntegrations[ selectedIntegration ].fieldSchema.map( i => {
                        return [ i.key, values[ i.key ] ];
                    } ) );
                    
                    setUploadingFile( true );
        
                    const response = await patchProxyObject( {  id: originalProxyObject.id, companyIntegrationId: integrationId, fieldData: fieldsData } );
                    const success = !( response?.error || response?.errors && response?.errors?.length > 0 );
                
                    if ( success ) { 
                        proxyObjectId = response; 
                        setProxySaveError( false );
                    } else {
                        setProxySaveError( true );
                        document.getElementById( 'proxy-card' ).scrollIntoView();
                        setUploadingFile( false );
                        return;
                    }
                } else {
                    fileAssetId = originalProxyAssetId;
                }
            }
        }
 
        const expireAt = dayjs( values[ 'expireAt' ] );
        
        const input = {
            id: objectId,
            duration: d == 0 ? 1 : d, 
            expireAt: expireAt.isValid() ? 
                expireAt.format() :
                null,
            format: values[ 'format' ], 
            locales: formLocales.filter( ( { locale } ) => cmxLocales.some( ( { code } ) => code === locale ) ),
            assets: 
            fileType === 'proxy' ?  
                [ {
                    id: fileAssetId,
                    isDefault: true,
                    mimetype: 'text/proxy',
                    locales: []
                } ]  :
                fileType === 'url' ?  
                    [ {
                        id: fileAssetId,
                        isDefault: true,
                        mimetype: 'text/uri-list',
                        locales: []
                    } ]  
                    :
                    fileType === 'file' ? 
                        fileAssetId ?  
                            [ {
                                id: fileAssetId,
                                isDefault: true,
                                name: selectedFile.name,
                                mimetype: selectedFile.type,
                                locales: []
                            } ]  : 
                            values[ 'fileAssets' ] ?  
                                values[ 'fileAssets' ].map( ( asset ) => ( {
                                    id: asset?.id,
                                    isDefault: true, 
                                    locales: asset?.locales,
                                    name: asset?.name,
                                    url: asset?.url,
                                    mimetype: asset?.mimetype,
                                    encoding: asset?.encoding,
                                } ) )
                                :
                                []
                        :
                        values[ 'scormAssets' ]?.map( ( asset ) => ( {
                            id: asset?.id,
                            isDefault: true, 
                            locales: asset?.locales,
                            name: asset?.name,
                            url: asset?.url,
                            mimetype: asset?.mimetype,
                            encoding: asset?.encoding,
                        } ) ),
            metadata: {
                ...LearningObjectUtil.getMetadata( previousData ),
                nonCMXLocales: formLocales.filter( ( { locale } ) => nonCmxLocales.some( ( { code } ) => code === locale ) )
            }
        };
        
        // Save the new learning object data.
        ( isEditing )
            ? await patchLearningObject( { variables: { input } } )
            : await createLearningObject( { variables: { input } } );

        // Handle completion.
        const hasError = ( patchError || createError );
        if ( !hasError ) {
            setTimeout( () => {
                messageApi.success( 'Learning Object Saved' );
                window.history.back();
                setUploadingFile( false );
            }, 6000 );
        } else {
            setUploadingFile( false );
        }
    };

    /**
     * Bools only used to show/hide DOM elements
     */ 

    const isLoading = useMemo( () => {
        return uploadingFile || fetchLearningObjectLoading || localesLoading || createLoading || patchLoading;
    }, [ uploadingFile, fetchLearningObjectLoading,  localesLoading, createLoading, patchLoading ] );
  
    const isInitilized = useMemo( () => {
        return objectId !== undefined;
    }, [ objectId ] );

    const hasError = useMemo( () => {
        if ( ( fetchLearningObjectCalled && fetchLearningObjectError ) ||
           ( createCalled && createError ) ||
           ( patchCalled && patchError )  
        ) {
            return true;
        } else {
            return false;
        }
    }, [ fetchLearningObjectError, fetchLearningObjectCalled, createCalled, createError, 
        patchCalled, patchError ] );

    const scormObjectId = useMemo( () => {
        if ( scormAssets && scormAssets.length > 0 ) {
            try {
                const matches = scormAssets[ 0 ].url?.match( /[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}/gi );

                if ( matches && matches.length > 0 ) {
                    const path = matches[ matches.length - 1 ];
                    return path;
                } else {
                    return '';
                }
            } catch ( e ) {
                console.error( e );
                return '';
            }
        } else {
            return '';
        }
    }, [ fileType, isLoading, fetchLearningObjectCalled, scormAssets ] );
        
    const [ fetchScormObject, { data: scormObjectData } ] = useLazyQuery( SCORM_COURSE_QUERY, 
        { variables: { scormCourseID: scormObjectId } }, { fetchPolicy: 'network-only' } );
        
    useEffect( () => {
        if ( scormObjectId && fetchLearningObjectCalled && !isLoading && fileType === 'scorm' ) {
            fetchScormObject();
        }
    }, [ fileType, isLoading, fetchLearningObjectCalled, scormObjectId ] );

    const scormZipURL = useMemo( () => {
        return  scormObjectData?.scormCourse?.node?.archiveUrl || '';
    }, [ scormObjectData  ] );

    return (
        <Layout className="create-edit-object">
            <Layout>
                { contextHolder }
                { isInitilized &&  
                <>
                    <PageHeader
                        title= { isEditing ? 'Edit Learning Object' : 'Create Learning Object' }
                        onBack={ () => window.history.back() }
                        breadcrumb={ buildPageHeaderBreadcrumb( [
                            { breadcrumbName: 'Course Studio', path: Routes.Home.buildPath() },
                            { breadcrumbName: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
                            { breadcrumbName: 'Learning Objects', path: Routes.Objects.buildPath() },
                            { breadcrumbName: isEditing ? 'Edit' : 'Create' },
                        ] ) }
                    />  
                    <ErrorPopup errors={ [ fetchLearningObjectError, localesError ] } />

                    <Loadable loading={ isLoading } >
                        <Section>
                            { hasError &&
                                <ApolloError { ...patchError } { ...createError } />
                            }

                            <Form  
                                form={ formController }
                                name="object-form"
                                layout="vertical"
                                initialValues={ initialFormValues }
                                onFinish={ handleFormSubmission }
                                scrollToFirstError
                            >
                                <Row gutter={ [ 24, 24 ] }>
                                    <Col xs={ 24 } lg={ 24 }>
                                        <Card title="General Settings">
                                            <Row gutter={ [ 24, 24 ] }>
                                                <Col xs={ 24 } lg={ 18 }>
                                                    <Form.Item
                                                        label="Type of Object"
                                                        name="format"
                                                        rules={ [
                                                            {
                                                                required: true,
                                                                message: 'Please select a type.',
                                                            },
                                                        ] }>
                                                        <Radio.Group
                                                            size="large"
                                                            disabled={ !canEdit } 
                                                            buttonStyle="solid"
                                                            onChange={ ( e ) => handleTypeChange( e.target.value ) }
                                                        >
                                                            { formatTypes.map( item => {
                                                                return (
                                                                    <Radio.Button value={ item } key={ item }>
                                                                        { item.toUpperCase() }
                                                                    </Radio.Button>
                                                                );
                                                            } ) }
                                                        </Radio.Group>
                                                    </Form.Item> 

                                                    <Form.Item 
                                                        label="Language"
                                                        name="locale"
                                                        rules={ [
                                                            {
                                                                message: 'You need to add a title and description for English (United States)',
                                                                validateTrigger: [],
                                                                validator: ( _, ) => {
                                                                    if ( currentLocaleCode === DEFAULT_LOCALE_CODE ) {
                                                                        return Promise.resolve();
                                                                    }
                                                                    
                                                                    if ( validateEnglishExists() ) {
                                                                        return Promise.resolve();
                                                                    } else {
                                                                        return Promise.reject();
                                                                    }
                                                                }
                                                            }
                                                        ] }
                                                    >
                                                        <LanguageSelector
                                                            mode="select-single"
                                                            onChange={ ( code ) => handleChangeLocale( code ) }
                                                        />
                                                    </Form.Item> 
                                                
                                                    <Form.Item
                                                        name="name"
                                                        label="Learning Object Name"
                                                        rules={ [
                                                            {
                                                                required: true,
                                                                message: 'Learning Objects are required to have a name.',
                                                            },
                                                            {
                                                                max: 255,
                                                                type: 'string',
                                                                message: 'Learning Object names can only be 250 characters long.',
                                                            }
                                                        ] }
                                                    >
                                                        <Input 
                                                            placeholder="Learning Object Name" 
                                                            disabled={ !canEdit } 
                                                        />
                                                    </Form.Item> 
                                                    
                                                    { /* Description */ }
                                                    { showTinyMCE && 
                                                        <Form.Item
                                                            name="description"
                                                            label="Content" 
                                                            rules={ [
                                                                {
                                                                    message: 'Learning Objects are required to have a description.',
                                                                    validateTrigger: [],
                                                                    validator: ( _, ) => {
                                                                        if ( TinyMCERef.current.getCharCount() > 0 ) {
                                                                            return Promise.resolve();
                                                                        } else {
                                                                            return Promise.reject();
                                                                        }
                                                                    }
                                                                },
                                                                {
                                                                    message: 'This content is too long. Please limit it to less that 500 characters.',
                                                                    validateTrigger: [],
                                                                    validator: ( _, ) => {
                                                                        if ( TinyMCERef.current.getCharCount() < 501 ) {
                                                                            return Promise.resolve();
                                                                        } else {
                                                                            return Promise.reject();
                                                                        }
                                                                    }
                                                                }
                                                            ] }    
                                                        >
                                                            <TinyMCE 
                                                                ref={ TinyMCERef } 
                                                                charLimit={ 500 }
                                                            />
                                                        </Form.Item> 
                                                    }

                                                    { !showTinyMCE &&
                                                        <Form.Item
                                                            name="description"
                                                            label="Learning Object Description"                                     
                                                            rules={ [
                                                                {
                                                                    required: true,
                                                                    message: 'Please provide a description.',
                                                                },
                                                            ] }
                                                        >
                                                            <RichTextEditor
                                                                readOnly={ !canEdit }
                                                                controls="simple"   
                                                                locale={ currentLocaleCode }
                                                                rawHTML={ descriptionRawHTML }
                                                            />
                                                        </Form.Item> 
                                                    }   

                                                    <Form.Item
                                                        label="Duration (in minutes)"
                                                        name="duration"
                                                        rules={ [
                                                            {
                                                                required: true,
                                                                message: 'Please provide a duration.',
                                                            },
                                                        ] }>
                                                        <InputNumber
                                                            min={ 1 }
                                                            size="large"
                                                            disabled={ !canEdit }
                                                        />
                                                    </Form.Item>

                                                    <Form.Item
                                                        label={ `Expiration Date (${ Intl.DateTimeFormat().resolvedOptions().timeZone })` }
                                                        name="expireAt"
                                                    >
                                                        <DatePicker
                                                            inputReadOnly
                                                            format="YYYY-MM-DD HH:mm"
                                                            showNow={ false }
                                                            showTime={ {
                                                                minuteStep: 30,
                                                                defaultValue: dayjs( '00:00', 'HH:mm' )
                                                            } }
                                                            disabledDate={ current =>
                                                                !current || 
                                                                current.isSameOrBefore( dayjs() )
                                                            }
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </Card>
                                    </Col>
                                </Row>   
                            
                                <Row gutter={ [ 24, 24 ] }>
                                    <Col xs={ 24 } lg={ 24 }>
                                        { fileType === 'file' &&
                                            <Card 
                                                title="File"
                                                className={ hasFileError ? 'file-error' : '' }
                                            >
                                                <Form.Item
                                                    name="fileAssets"
                                                >   
                                                    <FileUpload 
                                                        accept=".mp4,.mp3,.pdf,.ppt,.pptx,.xls,.xlsx,.doc,.docx,.jpg,.png,.svg,.gif"
                                                        onSelectFileCallback={ handleSelectFile }
                                                    /> 
                                                </Form.Item> 
                                                
                                                {
                                                    ( lxpFileURL && !hideDownloadButton ) &&
                                                    <p>
                                                        <strong>Original File: </strong>
                                                        <a target="_blank" href={ lxpFileURL } rel="noreferrer">Download</a>
                                                    </p>
                                                }

                                                <p><strong>Accepted file Types: </strong>.mp4, .mp3, .pdf, .ppt, .pptx, .xls, .xlsx, .doc, .docx, .jpg, .png, .svg, .gif</p>
                                                <p>Files larger than 1gb may need technical assistance, contact support if you experience upload issue.</p>
                                            </Card>
                                        }

                                        { fileType === 'scorm' &&
                                            <Card 
                                                title="Scorm"
                                                className={ hasFileError ? 'file-error' : '' }
                                            >
                                                <Form.Item
                                                    name="scormAssets"
                                                >
                                                    <AssetUpload 
                                                        accept=".zip"
                                                        type= "scorm"
                                                        onChange= { ( values ) => {
                                                            if ( values.length === 0 ) {
                                                                setScormAssets( [] );
                                                            }
                                                            setHasFileError( false );
                                                        } }
                                                    />
                                                </Form.Item>

                                                {
                                                    scormZipURL &&
                                                    <p>
                                                        <strong>Scorm zip archive: </strong>
                                                        <a href={ scormZipURL }>Download</a>
                                                    </p>
                                                }
                                                <p><strong>Accepted file Types: </strong>.zip</p>
                                            </Card>
                                        }
                                        
                                        { fileType === 'url' &&
                                            <Card 
                                                title="URL"
                                                className={ hasFileError ? 'file-error' : '' }
                                            >
                                                <p>URLs must be the full path to the file starting with <strong>http://</strong> or <strong>https://</strong>.</p>
                                            
                                                <Form.Item
                                                    name="url"
                                                    rules={ [
                                                        {
                                                            required: true,
                                                            message: 'You must add a URL.',
                                                        },
                                                        {
                                                            max: 2048,
                                                            type: 'string',
                                                            message: 'URLs can only be 2048 characters long.',
                                                        },
                                                        {
                                                            message: 'URLs must start with http:// or https://',
                                                            validator: ( _, value ) => {
                                                                if ( /^(http|https):\/\//.test( value ) ) {
                                                                    return Promise.resolve();
                                                                } else {
                                                                    return Promise.reject( 'URLs must start with http:// or https://' );
                                                                }
                                                            }
                                                        }
                                                    ] }
                                                >
                                                    <Input 
                                                        placeholder="http://" 
                                                        disabled={ !canEdit } 
                                                    />
                                                </Form.Item>
                                            </Card>
                                        }

                                        { fileType === 'proxy' &&
                                            <Card 
                                                id="proxy-card"
                                                title="Proxy" 
                                            >
                                                {
                                                    proxySaveError &&
                                                    <Alert
                                                        style={ { marginBottom: '20px' } }
                                                        message="Error, invalid value(s)"
                                                        description="One or more of these proxy settings is invalid."
                                                        type="error"
                                                    />
                                                }
                                                <div className="proxy-selector"> 
                                                    <p>Proxy Service</p>
                                                    <Select 
                                                        onChange={ handleIntegrationChange }
                                                        value={ selectedIntegration }
                                                    >
                                                        { proxyCompanyIntegrations.map( ( i, index ) => {
                                                            return ( 
                                                                <Select.Option 
                                                                    key={ i.id }
                                                                    value={ index }
                                                                >
                                                                    { i.name }
                                                                </Select.Option>
                                                            );
                                                        } ) }
                                                    </Select>
                                                </div>
                                                
                                                {
                                                    proxyCompanyIntegrations[ selectedIntegration ] &&
                                                    proxyCompanyIntegrations[ selectedIntegration ].fieldSchema.map( item => { 
                                                        if ( item.inputType === 'text' ) {
                                                            return (
                                                                <Form.Item
                                                                    key={ item.key }
                                                                    name={ item.key }
                                                                    label={ item.label }
                                                                    rules={ getProxyItemRules( item ) }
                                                                >
                                                                    <Input 
                                                                        placeholder={ item.placeholder }
                                                                        disabled={ !canEdit } 
                                                                    />
                                                                </Form.Item> 
                                                            );
                                                        }
                                                        
                                                        if ( item.inputType === 'url' ) {
                                                            return (
                                                                <Form.Item
                                                                    key={ item.key }
                                                                    name={ item.key }
                                                                    label={ item.label }
                                                                    rules={ getProxyItemRules( item ) }
                                                                >
                                                                    <Input 
                                                                        placeholder={ item.placeholder }
                                                                        disabled={ !canEdit } 
                                                                    />
                                                                </Form.Item> 
                                                            );
                                                        }

                                                        if ( item.inputType === 'numeric' ) {
                                                            return (
                                                                <Form.Item
                                                                    key={ item.key }
                                                                    name={ item.key }
                                                                    label={ item.label }
                                                                    rules={ getProxyItemRules( item ) }
                                                                >
                                                                    <InputNumber  
                                                                        type="number"
                                                                        disabled={ !canEdit } 
                                                                        onKeyPress={ ( event ) => {
                                                                            if ( !/[0-9]/.test( event.key ) ) {
                                                                                event.preventDefault();
                                                                            }
                                                                        } }
                                                                    />
                                                                </Form.Item> 
                                                            );
                                                        }
                                                    } )
                                                }
                                            </Card>
                                        }
                                    </Col>
                                </Row> 
                            </Form>        
                        </Section>
                    </Loadable>
                </>
                }  
            </Layout> 
            
            <Layout.Sider className="side-action-bar" width={ 100 } style={ { background: 'transparent' } }>
                <Affix  offsetTop={ 98 } style={ { marginTop: '98px' } }>
                    <Space direction="vertical" size={ 16 } style={ { width: '100%' } }>
                        { canEdit && 
                            <Button
                                disabled={ isLoading }
                                type="primary"
                                htmlType="submit"
                                style={ { width: '100%' } }
                                onClick={ () => {
                                    formController.submit();
                                } }
                            >
                               Save
                            </Button>   
                        }
                        <Button
                            disabled={ isLoading }
                            style={ { width: '100%' } }
                            onClick={ () => window.history.back() }
                        >
                            Cancel
                        </Button>
                        { ( isEditing && canDelete && courseCount === 0 ) &&
                            <DeleteButton
                                disabled={ isLoading }
                                itemType="Object"
                                onDelete={ handleDelete }
                                itemName={ originalName }
                                danger
                            >
                                Delete
                            </DeleteButton>
                        }
                        { ( isEditing && canDelete && courseCount > 0 ) &&
                            <Popover 
                                placement="topRight"
                                content={ ( <span>{ `This object cannot be deleted because it is being used by ${ courseCount } courses.` }</span> ) } 
                            >
                                <Button 
                                    style={ { width: '100%' } }
                                    disabled
                                >
                                    Delete
                                </Button>
                            </Popover>
                        }
                    </Space>
                </Affix>
            </Layout.Sider>
        </Layout>
    );
};

export default CreateEditObjectView;

